import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@l2u/ui/lib/api.ts';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { canWrite } from '../lib/adminAccess.ts';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { Modal } from '@l2u/ui/components/Modal.tsx';
import { NavIcon } from '@l2u/ui/components/NavIcon.tsx';
import { LOW_CONFIDENCE_THRESHOLD } from '@l2u/shared/docAnalysis';
import type { SubmissionFile } from '@l2u/ui/lib/types.ts';
import { groupAnalysesByFile, type DocumentAnalysis } from './documentAnalyses.ts';

// One fetch per company (React Query dedupes on the key across every card on
// the page); each card picks its file's rows out of the shared result.
//
// `pending` is surfaced, not swallowed: FileAnalysisModal must be able to tell
// "this document has no analysis" from "the analyses haven't arrived yet", or an
// early open would report emptiness about a document that does have a verdict.
function useCompanyAnalyses(companyId: string): {
  byFile: Map<string, DocumentAnalysis[]>;
  pending: boolean;
} {
  const { data, isPending } = useQuery({
    queryKey: ['document-analyses', companyId],
    queryFn: () =>
      adminApi.get<{ analyses: DocumentAnalysis[] }>(
        `/api/companies/${companyId}/document-analyses`,
      ),
    staleTime: 30_000,
  });
  return { byFile: groupAnalysesByFile(data?.analyses ?? []), pending: isPending };
}

function useFileAnalyses(companyId: string, fileId: string): DocumentAnalysis[] {
  return useCompanyAnalyses(companyId).byFile.get(fileId) ?? [];
}

/**
 * Advisory AI verdict for one uploaded file: newest run rendered in full,
 * prior runs collapsed. Renders nothing when the file has no analysis rows,
 * so the review UI is unchanged while the feature is off.
 */
export function FileAnalysisCard({
  companyId,
  submissionId,
  fileId,
}: {
  companyId: string;
  submissionId: string;
  fileId: string;
}) {
  const analyses = useFileAnalyses(companyId, fileId);
  if (analyses.length === 0) return null;
  const [current, ...history] = analyses;
  return (
    <div className="mt-1 w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
      <AnalysisBody
        analysis={current!}
        companyId={companyId}
        submissionId={submissionId}
        fileId={fileId}
      />
      {history.length > 0 && <AnalysisHistory history={history} />}
    </div>
  );
}

/** The files of `files` that actually have at least one analysis run, plus
 *  whether the analyses are still loading (empty vs. not-yet-known). */
function useAnalysedFiles(
  companyId: string,
  files: SubmissionFile[],
): { analysed: SubmissionFile[]; pending: boolean } {
  const { byFile, pending } = useCompanyAnalyses(companyId);
  return { analysed: files.filter((f) => (byFile.get(f.id) ?? []).length > 0), pending };
}

/**
 * Actions-cell button opening the AI verdict for the Compliance Documents tab.
 * That tab is the ONLY review surface for a renewal (the questionnaire row
 * freezes to the originally-approved file, see frozenApprovedDoc), so without
 * this the reviewer would have nowhere to see the verdict for the document they
 * are actually judging.
 *
 * Unconditional on any row that has an upload, including rows with no analysis
 * run at all — the modal reports the emptiness (2026-08-18). This deliberately
 * reverses the original "no run means no button": a hidden button made "the AI
 * found nothing" indistinguishable from "the AI never looked", which is not a
 * distinction a reviewer can afford to guess at.
 *
 * Note what this component does NOT do: it reads no query. Gating the button on
 * the analyses fetch would make it appear a beat after the row paints and shift
 * the neighbouring buttons under the reviewer's cursor.
 */
export function FileAnalysisButton({
  companyId,
  submissionId,
  files,
  docLabel,
}: {
  companyId: string;
  submissionId: string;
  files: SubmissionFile[];
  docLabel: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Btn
        size="sm"
        variant="secondary"
        aria-label={`AI analysis for "${docLabel}"`}
        title="AI analysis"
        onClick={() => setOpen(true)}
      >
        <NavIcon name="sparkles" size={16} />
      </Btn>
      {/* Not portalled, unlike this row's other modals: those portal because a
          <div> parented to a <tr> is invalid markup, whereas this one renders
          inside a <td> where it is fine. Modal is position:fixed, so the table
          cannot affect its layout — and staying in-tree is what keeps this
          component renderable under the app's markup-only test harness. */}
      {open && (
        <FileAnalysisModal
          companyId={companyId}
          submissionId={submissionId}
          files={files}
          docLabel={docLabel}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/**
 * The verdict(s) for one document, one card per analysed file — and the plain
 * statement that there are none when that is the case.
 *
 * Takes ALL of the document's live files and filters them itself. It has to: a
 * FileAnalysisCard for an unanalysed file renders nothing, so passing an
 * unfiltered list straight to the map would leave a file-name heading with
 * nothing under it, and would count that file towards the "name each file"
 * threshold. The filter used to live in FileAnalysisButton, which no longer
 * looks at the analyses at all.
 */
export function FileAnalysisModal({
  companyId,
  submissionId,
  files,
  docLabel,
  onClose,
}: {
  companyId: string;
  submissionId: string;
  files: SubmissionFile[];
  docLabel: string;
  onClose: () => void;
}) {
  const { analysed, pending } = useAnalysedFiles(companyId, files);
  return (
    <Modal
      open
      size="lg"
      title={`AI analysis — ${docLabel}`}
      onClose={onClose}
      footer={
        <Btn variant="secondary" size="sm" onClick={onClose}>
          Close
        </Btn>
      }
    >
      {pending ? (
        <div className="text-xs text-slate-500">Loading AI analysis…</div>
      ) : analysed.length === 0 ? (
        // Stated rather than left blank: an empty popup reads as broken, and the
        // reviewer's actual question is "did the AI look at this?", which a bare
        // white box does not answer.
        <div className="text-xs text-slate-500">No AI analysis for this document.</div>
      ) : (
        analysed.map((file) => (
          <div key={file.id} className="mb-3 last:mb-0">
            {/* Out of the table row's context two stacked verdicts are ambiguous,
                so a multi-file document names each one. A single file needs no
                heading — the modal title already says which document this is. */}
            {analysed.length > 1 && (
              <div className="mb-1 truncate text-xs font-semibold text-slate-500">
                {file.file_name}
              </div>
            )}
            <FileAnalysisCard companyId={companyId} submissionId={submissionId} fileId={file.id} />
          </div>
        ))
      )}
    </Modal>
  );
}

function AnalysisBody({
  analysis,
  companyId,
  submissionId,
  fileId,
}: {
  analysis: DocumentAnalysis;
  companyId: string;
  submissionId: string;
  fileId: string;
}) {
  if (analysis.status === 'queued' || analysis.status === 'running') {
    return <div className="text-slate-500">AI analysis pending…</div>;
  }
  if (analysis.status === 'skipped') {
    return (
      <div className="text-slate-500" title={analysis.error ?? undefined}>
        Not analyzed — unsupported file type
      </div>
    );
  }
  if (analysis.status === 'failed') {
    return (
      <div className="flex items-center justify-between gap-2">
        <span className="text-red-700">
          AI analysis failed{analysis.error ? `: ${analysis.error}` : ''}
        </span>
        <RetryButton companyId={companyId} submissionId={submissionId} fileId={fileId} />
      </div>
    );
  }
  const lowConfidence =
    analysis.confidence !== null && analysis.confidence < LOW_CONFIDENCE_THRESHOLD;
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`rounded-full px-2 py-0.5 font-semibold uppercase tracking-wide ${
            analysis.decision === 'approve'
              ? 'bg-emerald-100 text-emerald-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          AI: {analysis.decision === 'approve' ? 'Approve' : 'Reject'}
        </span>
        {analysis.document_type && <span className="text-slate-500">{analysis.document_type}</span>}
        {analysis.confidence !== null && (
          <span className="flex items-center gap-1.5">
            <span
              className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-200"
              role="meter"
              aria-valuenow={analysis.confidence}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="AI confidence"
            >
              <span
                className={`block h-full ${lowConfidence ? 'bg-amber-500' : 'bg-emerald-500'}`}
                style={{ width: `${Math.min(100, Math.max(0, analysis.confidence))}%` }}
              />
            </span>
            <span className="text-slate-600">{analysis.confidence}%</span>
            {lowConfidence && (
              <span className="font-medium text-amber-700">Low — review manually</span>
            )}
          </span>
        )}
      </div>
      {analysis.summary && <div className="text-slate-700">{analysis.summary}</div>}
      {analysis.extraction_note && (
        <div className="italic text-slate-500">{analysis.extraction_note}</div>
      )}
      {(analysis.criteria_results ?? []).length > 0 && (
        <ul className="space-y-1">
          {analysis.criteria_results!.map((cr, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <span
                className={`mt-px shrink-0 rounded px-1 font-semibold ${
                  cr.met ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
                }`}
              >
                {cr.met ? 'MET' : 'NOT MET'}
              </span>
              <span className="text-slate-700">
                {cr.criterion}
                {cr.evidence && <span className="text-slate-500"> — {cr.evidence}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RetryButton({
  companyId,
  submissionId,
  fileId,
}: {
  companyId: string;
  submissionId: string;
  fileId: string;
}) {
  const { user } = useAdminAuth();
  const qc = useQueryClient();
  const retryMutation = useMutation({
    mutationFn: () =>
      adminApi.post(
        `/api/companies/${companyId}/submissions/${submissionId}/files/${fileId}/analyses`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['document-analyses', companyId] }),
  });
  // Re-running an analysis is a write (POST .../analyses). The failure message
  // beside it stays visible to every tier -- only the recovery action goes.
  //
  // Checked AFTER every hook, never before one: `user` starts undefined while
  // auth resolves, so an early return here would change the hook count between
  // renders the moment a real role arrived and React would throw.
  if (!canWrite(user?.role)) return null;
  return (
    <Btn
      size="sm"
      variant="secondary"
      onClick={() => retryMutation.mutate()}
      loading={retryMutation.isPending}
    >
      Retry
    </Btn>
  );
}

function AnalysisHistory({ history }: { history: DocumentAnalysis[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1.5 border-t border-slate-200 pt-1.5">
      <button
        type="button"
        className="text-slate-500 hover:text-slate-800"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide' : 'Show'} {history.length} previous run{history.length === 1 ? '' : 's'}
      </button>
      {open && (
        <ul className="mt-1 space-y-1 text-slate-500">
          {history.map((a) => (
            <li key={a.id}>
              {new Date(a.created_at).toLocaleString()} — {a.trigger} —{' '}
              {a.status === 'succeeded'
                ? `${a.decision}${a.confidence !== null ? ` (${a.confidence}%)` : ''}`
                : a.status}
              {a.error ? ` — ${a.error}` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
